import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { sendGTMEvent } from '@next/third-parties/google';
import { RollbarContext, Provider as RollbarProvider } from '@rollbar/react';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { useEffect, useState } from 'react';
import { createEmotionSsrAdvancedApproach } from 'tss-react/next/pagesDir';
import { useStore } from 'zustand';

import { getFeatureFlags } from '@/clients/pxe-backend/featureFlags';
import PublicBatteryEcommBoundary from '@/components/BatteryEcomm/PublicRetrofit/PublicBatteryEcommBoundary';
import HTMLHead from '@/components/Common/HTMLHead';
import Cart from '@/components/SunrunShop/Cart/Cart';
import AppThemeProvider from '@/components/theme/AppThemeProvider';
import { PageContentType } from '@/contentful/contentful-exports';
import ReduxStoreProvider from '@/data-store/redux/reduxStoreProvider';
import type { FlagType } from '@/data-store/zustand/slices/globalSlice';
import { createAppBoundStore } from '@/data-store/zustand/zustand-store';
import { ZustandStoreProvider } from '@/data-store/zustand/zustandStoreProvider';
import { useHeaderHeightCssVar } from '@/hooks/useHeaderHeightCssVar';
import { rollbarConfig } from '@/rollbar/config';
import { captureAttributionParams } from '@/utils/attribution/attribution';
import { captureFirstTouchUrl } from '@/utils/firstTouchUrl/firstTouchUrl';
import { startFullstoryWhenReady } from '@/utils/fullstory/fullstory';
import { isPublicBatteryRoute } from '@/utils/publicBatteryEcomm/pageUrls';
import { isV4PageContentType, isV4Route } from '@/utils/theme/isV4Route';

import '@/styles/globals.css';
import { makeReduxStore } from '@/data-store/redux/redux-store';

type AddProvidersType = {
  addPreview: boolean;
  isStaticPath: boolean;
  useV4Theme: boolean;
  initialProps: { featureFlags: FlagType };
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  pageProps: { pageEntry: any; preview: boolean; pageContent: any };
} & Pick<AppProps, 'Component'>;

// Initialize app store singletons for zustand and redux
const zustandAppStore = createAppBoundStore();
const reduxAppStore = makeReduxStore();

const addProviders = ({
  Component,
  pageProps,
  addPreview,
  isStaticPath,
  useV4Theme,
  initialProps = { featureFlags: {} },
}: AddProvidersType) => {
  const pageContent = pageProps?.pageContent?.fields;
  const shouldLoadAbTasty =
    pageContent?.intellimizeSnippetEnabled === undefined
      ? true
      : pageContent.intellimizeSnippetEnabled;

  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let location;

  if (isStaticPath) {
    location = typeof window !== 'undefined' ? window?.location?.pathname : '';
  } else {
    location = pageContent?.path;
  }

  const nestedType = pageContent?.pageContent?.sys?.contentType?.sys?.id;
  const ogType = nestedType === PageContentType.BLOG ? 'article' : 'website';

  const { setFeatureFlags } = useStore(
    zustandAppStore,
    (state) => state.global.actions
  );

  // Runs in an effect, not during render, to avoid updating the zustand
  // store while MyApp is still rendering.
  useEffect(() => {
    if (initialProps.featureFlags) {
      setFeatureFlags(initialProps.featureFlags);
    }
  }, [initialProps.featureFlags, setFeatureFlags]);

  const mainContent = (
    <RollbarContext context={location}>
      {/* Top level hidden field for Journaya to populate leadId token */}
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <input type='hidden' id='leadid_token' name='universal_leadid' />
      <NuqsAdapter>
        <ZustandStoreProvider store={zustandAppStore}>
          <ReduxStoreProvider reduxStore={reduxAppStore}>
            {!useV4Theme && <Cart />}
            <PublicBatteryEcommBoundary>
              <Component {...pageProps} />
            </PublicBatteryEcommBoundary>
          </ReduxStoreProvider>
        </ZustandStoreProvider>
      </NuqsAdapter>
    </RollbarContext>
  );

  const withTheme = (
    <AppThemeProvider useV4Theme={useV4Theme}>
      <HTMLHead
        seoFields={pageContent?.seoFields}
        currentPath={location}
        shouldLoadAbTasty={shouldLoadAbTasty}
        ogType={ogType}
      />
      {mainContent}
    </AppThemeProvider>
  );

  const withOptionalPreview = addPreview ? (
    <ContentfulLivePreviewProvider
      locale='en-US'
      enableLiveUpdates={pageProps.preview}
    >
      {withTheme}
    </ContentfulLivePreviewProvider>
  ) : (
    withTheme
  );

  return (
    <RollbarProvider config={rollbarConfig}>
      {withOptionalPreview}
    </RollbarProvider>
  );
};

interface MyAppProps extends AppProps {
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  pageProps: { pageEntry: any; preview: boolean; pageContent: any };
}

function MyApp({ Component, pageProps }: MyAppProps) {
  const router = useRouter();
  useHeaderHeightCssVar();
  const [flags, setFlags] = useState<FlagType>();
  const [fetchedFlags, setFetchedFlags] = useState<FlagType>();

  useEffect(() => {
    getFeatureFlags()
      .then((response) => {
        setFetchedFlags(response.data);
      })
      .catch((err) => {
        console.warn('Feature flags unavailable:', err);
      });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    if (!router.isReady) return;

    const queryParams = router.query;

    if (
      queryParams.featureFlag &&
      queryParams.featureFlagValue &&
      fetchedFlags
    ) {
      setFlags({
        ...fetchedFlags,
        [queryParams.featureFlag as string]:
          queryParams.featureFlagValue as string,
      });
    } else {
      setFlags(fetchedFlags);
    }

    if (queryParams.clearFlag === 'true') {
      getFeatureFlags()
        .then((response) => {
          setFlags(response.data);
        })
        .catch((err) => {
          console.warn('Feature flags unavailable:', err);
        });
    }
  }, [fetchedFlags]);

  useEffect(() => sendGTMEvent({ event: 'optimize.activate' }), []);
  useEffect(() => captureAttributionParams(router.query), [router.query]);
  useEffect(() => captureFirstTouchUrl(), []);

  // The /shop/battery pages don't render the Page component, which is the
  // only other place Fullstory capture is started.
  useEffect(() => {
    if (!isPublicBatteryRoute(router.pathname)) return;
    return startFullstoryWhenReady();
  }, [router.pathname]);

  const isInEcomm = router.pathname.startsWith('/shop/solar');
  const useV4Theme =
    isV4Route(router.pathname) ||
    isV4PageContentType(
      pageProps?.pageContent?.fields?.pageContent?.sys?.contentType?.sys?.id
    );
  const addPreview = pageProps?.preview || false;

  return addProviders({
    Component,
    pageProps,
    addPreview,
    isStaticPath: isInEcomm,
    useV4Theme,
    initialProps: { featureFlags: flags },
  });
}

const { augmentDocumentWithEmotionCache, withAppEmotionCache } =
  createEmotionSsrAdvancedApproach({ key: 'css' });

export default withAppEmotionCache(MyApp);
export { augmentDocumentWithEmotionCache };
